class PdpVideoCarousel extends HTMLElement {
  connectedCallback() {
    this._track      = this.querySelector('.pdp-video-carousel__track');
    this._items      = Array.from(this.querySelectorAll('.pdp-video-carousel__item'));
    this._dotsEl     = this.querySelector('.pdp-video-carousel__dots');
    this._controlsEl = this.querySelector('.pdp-video-carousel__controls');
    this._prevBtn    = this.querySelector('.pdp-video-carousel__prev');
    this._nextBtn    = this.querySelector('.pdp-video-carousel__next');
    this._activePage = 0;
    this._dots       = [];

    if (!this._track || !this._items.length) return;

    this._bindCarouselEvents();
    this._bindPlayEvents();

    // Defer until after layout so item widths are measured correctly
    requestAnimationFrame(() => {
      this._buildDots();
      this._updateControls();
      const ro = new ResizeObserver(() => this._onResize());
      ro.observe(this._track);
    });
  }

  // ── Pages ────────────────────────────────────────────────────

  _itemsPerView() {
    if (!this._items.length) return 1;
    const style  = getComputedStyle(this._track);
    const pl     = parseFloat(style.paddingLeft)  || 0;
    const pr     = parseFloat(style.paddingRight) || 0;
    const gap    = parseFloat(style.columnGap)    || 0;
    const visW   = this._track.clientWidth - pl - pr;
    const itemW  = this._items[0].offsetWidth;
    if (!itemW) return 1;
    return Math.max(1, Math.round((visW + gap) / (itemW + gap)));
  }

  _pageCount() {
    return Math.max(1, Math.ceil(this._items.length / this._itemsPerView()));
  }

  _buildDots() {
    if (!this._dotsEl) return;

    const count = this._pageCount();
    const hide  = count <= 1;

    this._dotsEl.hidden = hide;
    if (this._controlsEl) this._controlsEl.hidden = hide;

    this._dotsEl.innerHTML = '';
    this._dots = [];

    for (let i = 0; i < count; i++) {
      const btn = document.createElement('button');
      btn.className = 'pdp-video-carousel__dot';
      if (i === 0) btn.classList.add('pdp-video-carousel__dot--active');
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', `Go to page ${i + 1}`);
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.setAttribute('data-index', i);
      btn.type = 'button';
      this._dotsEl.appendChild(btn);
      this._dots.push(btn);
    }
  }

  _onResize() {
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const newCount = this._pageCount();
      if (this._dots.length !== newCount) {
        this._activePage = 0;
        this._buildDots();
        this._updateControls();
      }
    }, 150);
  }

  // ── Carousel navigation ──────────────────────────────────────

  _bindCarouselEvents() {
    this._prevBtn?.addEventListener('click', () => this._navigate(-1));
    this._nextBtn?.addEventListener('click', () => this._navigate(1));

    this._dotsEl?.addEventListener('click', (e) => {
      const btn = e.target.closest('.pdp-video-carousel__dot');
      if (btn) this._goToPage(Number(btn.dataset.index));
    });

    let scrollTimer;
    const onScroll = () => {
      // Skip debounced sync during programmatic scrollTo — active page is already set correctly
      if (this._isProgrammaticScroll) return;
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => this._syncActiveFromScroll(), 120);
    };
    this._track.addEventListener('scroll', onScroll, { passive: true });
    this._track.addEventListener('scrollend', () => {
      clearTimeout(scrollTimer);
      const wasProgrammatic = this._isProgrammaticScroll;
      this._isProgrammaticScroll = false;
      // Only sync from scroll for user-initiated swipes — programmatic nav already set activePage
      if (!wasProgrammatic) this._syncActiveFromScroll();
    });
  }

  _navigate(dir) {
    this._goToPage(this._activePage + dir);
  }

  _goToPage(page) {
    const count   = this._pageCount();
    const clamped = Math.max(0, Math.min(page, count - 1));
    const itemIdx = clamped * this._itemsPerView();
    const item    = this._items[Math.min(itemIdx, this._items.length - 1)];

    // Prevent scroll events from overwriting activePage while animation is running
    this._isProgrammaticScroll = true;
    clearTimeout(this._progScrollTimer);
    this._progScrollTimer = setTimeout(() => { this._isProgrammaticScroll = false; }, 800);

    // scrollIntoView respects scroll-padding-left and snap points natively
    item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });

    this._activePage = clamped;
    this._updateControls();
  }

  _syncActiveFromScroll() {
    const trackLeft  = this._track.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDist  = Infinity;

    this._items.forEach((item, i) => {
      const dist = Math.abs(item.getBoundingClientRect().left - trackLeft);
      if (dist < closestDist) { closestDist = dist; closestIndex = i; }
    });

    this._activePage = Math.floor(closestIndex / this._itemsPerView());
    this._updateControls();
  }

  _updateControls() {
    const count = this._pageCount();

    this._dots.forEach((dot, i) => {
      const active = i === this._activePage;
      dot.classList.toggle('pdp-video-carousel__dot--active', active);
      dot.setAttribute('aria-selected', String(active));
    });

    if (this._prevBtn) this._prevBtn.disabled = this._activePage === 0;
    if (this._nextBtn) this._nextBtn.disabled = this._activePage === count - 1;
  }

  // ── Video play / mute ────────────────────────────────────────

  _bindPlayEvents() {
    this._items.forEach((item) => {
      const playBtn = item.querySelector('.pdp-video-carousel__play-btn');
      playBtn?.addEventListener('click', () => this._playVideo(item));
    });
  }

  _playVideo(item) {
    const wrap = item.querySelector('.pdp-video-carousel__video-wrap');
    const { videoId, videoType } = wrap.dataset;

    if (!videoType) return;

    if (videoType === 'cdn') {
      this._playCdnVideo(wrap);
      return;
    }

    if (!videoId) return;

    const src = this._buildEmbedUrl(videoId, videoType);
    if (!src) return;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.className = 'pdp-video-carousel__iframe';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', 'Video player');

    const unmuteBtn = this._buildUnmuteBtn(iframe, videoType);

    wrap.innerHTML = '';
    wrap.appendChild(iframe);
    wrap.appendChild(unmuteBtn);
  }

  _playCdnVideo(wrap) {
    const video   = wrap.querySelector('.pdp-video-carousel__native-video');
    const playBtn = wrap.querySelector('.pdp-video-carousel__play-btn');
    if (!video) return;

    if (!video.dataset.vcInit) {
      video.dataset.vcInit = '1';
      wrap.querySelector('.pdp-video-carousel__cover')?.remove();
      video.style.display = 'block';
      wrap.appendChild(this._buildCdnUnmuteBtn(video));
      video.addEventListener('play',  () => this._setPlayState(playBtn, true));
      video.addEventListener('pause', () => this._setPlayState(playBtn, false));
      video.addEventListener('ended', () => this._setPlayState(playBtn, false));
    }

    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  _setPlayState(playBtn, playing) {
    if (!playBtn) return;
    const playIcon  = playBtn.querySelector('.pdp-video-carousel__icon-play');
    const pauseIcon = playBtn.querySelector('.pdp-video-carousel__icon-pause');
    if (playIcon)  playIcon.hidden  = playing;
    if (pauseIcon) pauseIcon.hidden = !playing;
    playBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  }

  _buildCdnUnmuteBtn(video) {
    let isMuted = true;

    const btn = document.createElement('button');
    btn.className = 'pdp-video-carousel__unmute-btn';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Unmute video');
    btn.innerHTML = this._iconMuted();

    btn.addEventListener('click', () => {
      isMuted = !isMuted;
      video.muted = isMuted;
      btn.innerHTML = isMuted ? this._iconMuted() : this._iconUnmuted();
      btn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
    });

    return btn;
  }

  _buildEmbedUrl(id, type) {
    if (type === 'youtube') {
      return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&rel=0&playsinline=1&enablejsapi=1`;
    }
    if (type === 'vimeo') {
      return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&byline=0&portrait=0&title=0&api=1`;
    }
    return null;
  }

  _buildUnmuteBtn(iframe, videoType) {
    let isMuted = true;

    const btn = document.createElement('button');
    btn.className = 'pdp-video-carousel__unmute-btn';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Unmute video');
    btn.innerHTML = this._iconMuted();

    btn.addEventListener('click', () => {
      isMuted = !isMuted;
      this._postMuteCommand(iframe, videoType, isMuted);
      btn.innerHTML = isMuted ? this._iconMuted() : this._iconUnmuted();
      btn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
    });

    return btn;
  }

  _postMuteCommand(iframe, type, mute) {
    if (type === 'youtube') {
      const func = mute ? 'mute' : 'unMute';
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func, args: [] }),
        '*'
      );
    } else if (type === 'vimeo') {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'setMuted', value: mute }),
        'https://player.vimeo.com'
      );
    }
  }

  _iconMuted() {
    return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="currentColor"/>
    </svg>`;
  }

  _iconUnmuted() {
    return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" fill="currentColor"/>
    </svg>`;
  }
}

customElements.define('pdp-video-carousel', PdpVideoCarousel);
